import { Application, Container, Graphics, Text, TextStyle } from "pixi.js";
import { momentumVisuals, combatStateVisuals } from "./stateVisuals";
import { soulsmithColors } from "./visualTokens";

type HudMode = "desktop" | "mobile";

export interface HudAuditState {
  mode: HudMode;
  panels: Record<string, boolean>;
  momentumValue: number;
  staminaValue: number;
  systemLabels: string[];
  menuVisible: boolean;
  pillarCount: number;
}

const COLORS = {
  obsidian: 0x0a0a0f,
  deep: 0x050508,
  blood: 0x8b0000,
  darkBlood: 0x2a0506,
  gold: 0xd4af37,
  ash: 0x7a7a7a,
  bone: 0xded0b8,
  voidPurple: 0x3a164f
};

const PANEL_ALPHA = 0.9;
const SYSTEM_LABELS = ["Inventory", "Forge", "Coven", "Market", "Battle Pass", "Achievements", "Settings"];
const SYSTEM_KEYS = ["I", "F", "C", "M", "B", "A", "S"];

export class ObsidianHud {
  private app: Application;
  private shell = new Container();
  private pillars = new Container();
  private lastRenderKey = "";
  private lastMomentum = -1;
  private lastStamina = -1;
  private sectorName = "The God-Haunted Waste";
  private biome = "Skinlands";
  private menuVisible = false;
  private auditState: HudAuditState = {
    mode: "desktop",
    panels: {},
    momentumValue: 0,
    staminaValue: 100,
    systemLabels: SYSTEM_LABELS,
    menuVisible: false,
    pillarCount: 0
  };

  constructor(host: HTMLElement) {
    this.app = new Application({ resizeTo: window, backgroundAlpha: 0, antialias: false, resolution: Math.min(window.devicePixelRatio, 1.5) });
    const canvas = this.app.view as HTMLCanvasElement;
    canvas.dataset.soulsmithHudCanvas = "true";
    host.appendChild(canvas);
    this.app.stage.addChild(this.shell, this.pillars);
    window.addEventListener("resize", () => {
      this.lastRenderKey = "";
      this.render(this.lastMomentum < 0 ? 0 : this.lastMomentum, this.lastStamina < 0 ? 100 : this.lastStamina);
      if (this.menuVisible) this.drawPillars();
    });
    this.publishAudit();
  }

  setSector(biome: string, sectorName = "Sector 0:0") {
    this.biome = biome;
    this.sectorName = sectorName;
    this.lastRenderKey = "";
  }

  getAuditState() {
    return this.auditState;
  }

  render(momentumValue: number, staminaValue: number) {
    const width = this.app.renderer.width / this.app.renderer.resolution;
    const height = this.app.renderer.height / this.app.renderer.resolution;
    const mode: HudMode = width < 720 ? "mobile" : "desktop";
    const key = `${mode}:${Math.round(width)}:${Math.round(height)}:${Math.round(momentumValue)}:${Math.round(staminaValue)}:${this.biome}:${this.sectorName}`;
    if (key === this.lastRenderKey) return;

    this.lastRenderKey = key;
    this.lastMomentum = momentumValue;
    this.lastStamina = staminaValue;
    this.shell.removeChildren();

    if (mode === "mobile") {
      this.drawMobileShell(width, height, momentumValue, staminaValue);
    } else {
      this.drawDesktopShell(width, height, momentumValue, staminaValue);
    }

    this.auditState = {
      mode,
      panels: {
        identity: true,
        momentum: true,
        stamina: true,
        minimap: true,
        objective: true,
        story: true,
        chat: true,
        weapon: true,
        abilities: true,
        essence: true,
        systems: true
      },
      momentumValue,
      staminaValue,
      systemLabels: SYSTEM_LABELS,
      menuVisible: this.menuVisible,
      pillarCount: this.menuVisible ? 6 : 0
    };
    this.publishAudit();
  }

  showPillars() {
    this.menuVisible = true;
    this.drawPillars();
    this.auditState = { ...this.auditState, menuVisible: true, pillarCount: 6 };
    this.publishAudit();
  }

  hidePillars() {
    this.menuVisible = false;
    this.pillars.removeChildren();
    this.auditState = { ...this.auditState, menuVisible: false, pillarCount: 0 };
    this.publishAudit();
  }

  // Stage 6: Combat Visual Contract methods
  updateMomentumState(state: typeof momentumVisuals[keyof typeof momentumVisuals]) {
    // Momentum state is handled through render() with momentumValue parameter
    // This method can trigger additional visual treatments if needed
    this.render(this.lastMomentum, this.lastStamina);
  }

  spawnRuneMarker(marker: { x: number; y: number; timestamp: number; type: "light" | "heavy" }, type: "light" | "heavy") {
    const rune = new Graphics();
    const visuals = combatStateVisuals.movementMarker;
    const isMobile = this.auditState.mode === "mobile";
    const radius = isMobile ? visuals.mobile.radius : visuals.desktop.radius;
    
    rune.lineStyle(2, visuals.desktop.color, 0.8);
    rune.beginFill(visuals.desktop.color, 0.3);
    rune.drawCircle(marker.x, marker.y, radius);
    rune.endFill();
    
    rune.lineStyle(1, visuals.desktop.secondaryColor, 0.6);
    rune.drawCircle(marker.x, marker.y, radius * 0.6);
    
    this.shell.addChild(rune);
    
    // Animate and remove after duration
    setTimeout(() => {
      this.shell.removeChild(rune);
    }, type === "heavy" ? 800 : 400);
  }

  spawnStaticRuneMarker(marker: { x: number; y: number; timestamp: number; type: "light" | "heavy" }, type: "light" | "heavy") {
    const rune = new Graphics();
    const visuals = combatStateVisuals.movementMarker;
    const isMobile = this.auditState.mode === "mobile";
    const radius = isMobile ? visuals.mobile.radius : visuals.desktop.radius;
    
    rune.lineStyle(2, visuals.desktop.color, 0.8);
    rune.drawCircle(marker.x, marker.y, radius);
    
    this.shell.addChild(rune);
    
    // Static display for reduced motion
    setTimeout(() => {
      this.shell.removeChild(rune);
    }, 200);
  }

  setStaminaState(state: "normal" | "ash") {
    // Stamina state affects visual rendering
    if (state === "ash") {
      // Low stamina visual treatment
      this.render(this.lastMomentum, 20); // Force low stamina visual
    } else {
      this.render(this.lastMomentum, this.lastStamina);
    }
  }

  spawnHitVFX(visuals: typeof combatStateVisuals.lightAttack | typeof combatStateVisuals.heavyAttack) {
    const vfx = new Graphics();
    const sparkColor = "spark" in visuals ? visuals.spark : soulsmithColors.emberOrange;
    
    vfx.lineStyle(2, sparkColor, 0.9);
    
    // Create simple hit effect
    vfx.beginFill(sparkColor, 0.5);
    vfx.drawCircle(
      this.app.renderer.width / 2, 
      this.app.renderer.height / 2, 
      20
    );
    vfx.endFill();
    
    this.shell.addChild(vfx);
    
    setTimeout(() => {
      this.shell.removeChild(vfx);
    }, 200);
  }

  private drawDesktopShell(width: number, height: number, momentumValue: number, staminaValue: number) {
    this.drawIdentity(22, 18, 286, 142, false);
    this.drawCentralBars(width / 2 - 280, 18, 560, momentumValue, staminaValue, false);
    this.drawMinimap(width - 302, 18, 280, 196, false);
    this.drawObjective(22, 214, 284, 224, false);
    this.drawChat(22, height - 182, 330, 158, false);
    this.drawWeaponTray(width / 2 - 318, height - 154, 636, 132, false);
    this.drawSystemStack(width - 272, 238, 250, 332, false);
  }

  private drawMobileShell(width: number, height: number, momentumValue: number, staminaValue: number) {
    this.drawIdentity(8, 8, 146, 96, true);
    this.drawCentralBars(162, 8, Math.max(212, width - 170), momentumValue, staminaValue, true);
    this.drawObjective(8, 112, 218, 134, true);
    this.drawMinimap(width - 154, 88, 146, 116, true);
    this.drawSystemStack(width - 154, 216, 146, 262, true);
    this.drawChat(8, Math.max(520, height - 280), 178, 116, true);
    this.drawWeaponTray(8, height - 142, width - 16, 130, true);
  }

  private drawIdentity(x: number, y: number, width: number, height: number, mobile: boolean) {
    const panel = this.panel(x, y, width, height);
    this.shell.addChild(panel);
    const medallion = new Graphics();
    medallion.lineStyle(1, COLORS.gold, 1);
    medallion.beginFill(COLORS.deep, 0.96);
    this.drawJaggedDiamond(medallion, x + 42, y + 42, mobile ? 32 : 42);
    medallion.endFill();
    medallion.lineStyle(1, COLORS.blood, 1);
    medallion.moveTo(x + 42, y + 16);
    medallion.lineTo(x + 42, y + 68);
    medallion.moveTo(x + 20, y + 42);
    medallion.lineTo(x + 64, y + 42);
    this.shell.addChild(medallion);

    if (mobile) {
      this.text("ASH-BOUND", x + 78, y + 17, 8, COLORS.bone, width - 86, true);
      this.text("SMITH", x + 78, y + 30, 8, COLORS.bone, width - 86, true);
      this.text("LV 1", x + 78, y + 45, 8, COLORS.gold, width - 86, true);
    } else {
      this.text("ASH-BOUND SMITH", x + 92, y + 18, 16, COLORS.bone, width - 104, true);
      this.text("LEVEL 1", x + 92, y + 44, 12, COLORS.gold, width - 104, true);
    }
    this.bar(x + (mobile ? 12 : 92), y + (mobile ? 64 : 74), width - (mobile ? 24 : 112), mobile ? 7 : 10, 82, COLORS.blood);
    this.bar(x + (mobile ? 12 : 92), y + (mobile ? 78 : 92), width - (mobile ? 24 : 112), mobile ? 6 : 8, 64, COLORS.gold);
    if (!mobile) {
      this.text("POWER 8,742", x + 92, y + 112, 12, COLORS.ash, 150, false);
      this.drawBuffStrip(x + 196, y + 108, 5);
    }
  }

  private drawCentralBars(x: number, y: number, width: number, momentumValue: number, staminaValue: number, mobile: boolean) {
    const height = mobile ? 86 : 126;
    const panel = this.panel(x, y, width, height);
    this.shell.addChild(panel);
    this.text("MOMENTUM", x + 18, y + 12, mobile ? 10 : 14, COLORS.bone, width - 36, true, "center");
    this.text(`${Math.round(momentumValue)}%`, x + width / 2 - 28, y + (mobile ? 28 : 34), mobile ? 10 : 13, COLORS.bone, 56, true, "center");
    this.bar(x + 24, y + (mobile ? 44 : 58), width - 48, mobile ? 12 : 18, momentumValue, COLORS.blood);
    this.text("STAMINA", x + 18, y + (mobile ? 60 : 82), mobile ? 9 : 11, COLORS.gold, width - 36, true, "center");
    this.bar(x + 44, y + (mobile ? 72 : 100), width - 88, mobile ? 8 : 12, staminaValue, COLORS.gold);
  }

  private drawMinimap(x: number, y: number, width: number, height: number, mobile: boolean) {
    const panel = this.panel(x, y, width, height);
    this.shell.addChild(panel);
    this.text(mobile ? "SECTOR 0:0" : this.sectorName.toUpperCase(), x + 14, y + 12, mobile ? 9 : 13, COLORS.bone, width - 28, true);
    this.text(this.biome.toUpperCase(), x + 14, y + (mobile ? 28 : 32), mobile ? 8 : 10, this.biome === "Neural Reach" ? COLORS.voidPurple : COLORS.blood, width - 28, true);

    const map = new Graphics();
    const mx = x + 18;
    const my = y + (mobile ? 46 : 58);
    const mw = width - 36;
    const mh = height - (mobile ? 70 : 94);
    map.lineStyle(1, COLORS.ash, 0.72);
    map.beginFill(COLORS.deep, 0.74);
    map.drawPolygon([mx, my + 10, mx + mw * 0.22, my, mx + mw, my + 18, mx + mw * 0.86, my + mh, mx + mw * 0.18, my + mh - 8, mx, my + mh * 0.58]);
    map.endFill();
    map.lineStyle(1, COLORS.blood, 0.9);
    map.moveTo(mx + mw * 0.22, my + mh * 0.68);
    map.lineTo(mx + mw * 0.48, my + mh * 0.42);
    map.lineTo(mx + mw * 0.78, my + mh * 0.54);
    map.beginFill(COLORS.gold, 1);
    map.drawPolygon([mx + mw * 0.5, my + mh * 0.3, mx + mw * 0.56, my + mh * 0.42, mx + mw * 0.44, my + mh * 0.42]);
    map.endFill();
    this.shell.addChild(map);
    if (!mobile) {
      this.text("CORRUPTION: 62%", x + 16, y + height - 30, 10, COLORS.ash, width - 32, false);
      this.bar(x + 118, y + height - 27, width - 148, 5, 62, COLORS.blood);
    }
  }

  private drawObjective(x: number, y: number, width: number, height: number, mobile: boolean) {
    const panel = this.panel(x, y, width, height);
    this.shell.addChild(panel);
    this.text("THE CRUCIBLE CALLS", x + 16, y + 14, mobile ? 11 : 15, COLORS.gold, width - 32, true);
    this.line(x + 16, y + (mobile ? 34 : 44), width - 32, COLORS.gold, 0.35);
    this.text("Restore the Anvils of the Fallen.", x + 16, y + (mobile ? 44 : 58), mobile ? 9 : 11, COLORS.ash, width - 32, false);
    this.objectiveRow("Activate the Crucible Anvil", x + 18, y + (mobile ? 66 : 86), width - 36, true, mobile);
    this.objectiveRow("Collect God-Bone Shards (3/7)", x + 18, y + (mobile ? 86 : 112), width - 36, false, mobile);
    this.objectiveRow("Defeat the Remnant King", x + 18, y + (mobile ? 106 : 138), width - 36, false, mobile);
    if (!mobile) {
      this.line(x + 16, y + 166, width - 32, COLORS.ash, 0.34);
      this.text("STORY", x + 16, y + 178, 12, COLORS.blood, width - 32, true);
      this.text("Ashes Remember", x + 50, y + 202, 11, COLORS.gold, width - 66, false);
    }
  }

  private drawChat(x: number, y: number, width: number, height: number, mobile: boolean) {
    const panel = this.panel(x, y, width, height);
    this.shell.addChild(panel);
    const tabWidth = width / 3;
    ["Global", "Coven", "Sector"].forEach((tab, index) => {
      const tx = x + index * tabWidth;
      const tabGraphic = new Graphics();
      tabGraphic.lineStyle(1, index === 0 ? COLORS.blood : COLORS.ash, 0.72);
      tabGraphic.beginFill(index === 0 ? COLORS.darkBlood : COLORS.deep, 0.84);
      tabGraphic.drawPolygon([tx, y, tx + tabWidth - 4, y, tx + tabWidth - 9, y + 24, tx + 4, y + 24]);
      tabGraphic.endFill();
      this.shell.addChild(tabGraphic);
      this.text(tab, tx + 8, y + 6, mobile ? 8 : 10, index === 0 ? COLORS.gold : COLORS.ash, tabWidth - 16, true, "center");
    });
    this.text("[09:14] Vir: anvil up in S3", x + 12, y + 36, mobile ? 8 : 10, COLORS.bone, width - 24, false);
    this.text("[09:15] GrimForger: meet here", x + 12, y + (mobile ? 52 : 58), mobile ? 8 : 10, COLORS.ash, width - 24, false);
    this.text("[09:16] Echo of Kael: For the Crucible.", x + 12, y + (mobile ? 68 : 80), mobile ? 8 : 10, COLORS.gold, width - 24, false);
    if (!mobile) this.text("Enter message...", x + 12, y + height - 28, 10, COLORS.ash, width - 24, false);
  }

  private drawWeaponTray(x: number, y: number, width: number, height: number, mobile: boolean) {
    const panel = this.panel(x, y, width, height);
    this.shell.addChild(panel);
    const weaponWidth = mobile ? 104 : 150;
    const weapon = this.panel(x + 10, y + 14, weaponWidth, height - 28, 0.95, COLORS.gold);
    this.shell.addChild(weapon);
    this.text(mobile ? "GREATBLADE" : "CRUCIBLE GREATBLADE", x + 20, y + 24, mobile ? 8 : 10, COLORS.gold, weaponWidth - 20, true);
    const blade = new Graphics();
    blade.lineStyle(1, COLORS.blood, 1);
    blade.beginFill(COLORS.gold, 0.82);
    blade.drawPolygon([x + 36, y + height - 30, x + weaponWidth - 18, y + 50, x + weaponWidth - 2, y + 62, x + 54, y + height - 22]);
    blade.endFill();
    blade.beginFill(COLORS.blood, 0.82);
    blade.drawPolygon([x + 50, y + height - 38, x + weaponWidth - 32, y + 66, x + weaponWidth - 22, y + 72, x + 62, y + height - 30]);
    blade.endFill();
    this.shell.addChild(blade);

    const slotSize = mobile ? 34 : 52;
    const startX = x + weaponWidth + (mobile ? 18 : 28);
    ["Q", "W", "E", "R"].forEach((key, index) => {
      const sx = startX + index * (slotSize + (mobile ? 8 : 12));
      this.slot(sx, y + (mobile ? 22 : 30), slotSize, COLORS.blood, key);
    });
    ["1", "2", "3", "4"].forEach((key, index) => {
      const sx = startX + index * (slotSize + (mobile ? 8 : 12));
      this.slot(sx, y + (mobile ? 72 : 88), slotSize, index === 0 ? COLORS.blood : index === 1 ? COLORS.ash : index === 2 ? COLORS.voidPurple : COLORS.gold, key);
    });
    if (!mobile) {
      this.text("ESSENCE", x + width - 176, y + 20, 11, COLORS.bone, 150, true, "center");
      this.text("23   12   8   4", x + width - 170, y + 86, 12, COLORS.gold, 150, false, "center");
    }
  }

  private drawSystemStack(x: number, y: number, width: number, height: number, mobile: boolean) {
    const rowHeight = Math.floor(height / SYSTEM_LABELS.length) - 4;
    SYSTEM_LABELS.forEach((label, index) => {
      const sy = y + index * (rowHeight + 4);
      const selected = label === "Battle Pass";
      const panel = this.panel(x, sy, width, rowHeight, selected ? 0.94 : 0.88, selected ? COLORS.blood : COLORS.gold);
      this.shell.addChild(panel);
      const icon = new Graphics();
      icon.lineStyle(1, selected ? COLORS.gold : COLORS.ash, 1);
      icon.beginFill(selected ? COLORS.darkBlood : COLORS.deep, 0.88);
      this.drawJaggedDiamond(icon, x + 22, sy + rowHeight / 2, mobile ? 10 : 13);
      icon.endFill();
      this.shell.addChild(icon);
      this.text(label, x + (mobile ? 40 : 48), sy + rowHeight / 2 - (mobile ? 6 : 8), mobile ? 8 : 12, selected ? COLORS.gold : COLORS.bone, width - (mobile ? 66 : 78), true);
      this.text(SYSTEM_KEYS[index], x + width - 24, sy + rowHeight / 2 - 7, mobile ? 8 : 10, COLORS.gold, 16, true, "center");
    });
  }

  private drawPillars() {
    this.pillars.removeChildren();
    const pillars = [
      ["The Forge", "F", "Weapon creation, reforge, inventory"],
      ["The Ego", "C", "Character sheet, Facet tree, viewer"],
      ["The Cartography", "M", "3D world map, timers, Echo heatmap"],
      ["The Coven", "G", "Guild, vault, hunt, territory"],
      ["The Bazaar", "B", "IronBazaar, TheHollow, trading"],
      ["The Chronicle", "L", "Leaderboards, statues, weapon history"]
    ];
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const mobile = viewportWidth < 700;
    const columns = mobile ? 2 : 6;
    const gap = mobile ? 12 : 26;
    const pillarWidth = mobile ? Math.min(166, Math.floor((viewportWidth - 32 - gap) / 2)) : Math.min(154, Math.floor((viewportWidth - 124 - gap * 5) / 6));
    const pillarHeight = mobile ? Math.min(138, Math.floor((viewportHeight - 250) / 3)) : Math.min(386, viewportHeight - 304);
    const totalWidth = columns * pillarWidth + (columns - 1) * gap;
    const startX = Math.max(16, (viewportWidth - totalWidth) / 2);
    const startY = mobile ? 144 : Math.max(150, viewportHeight / 2 - pillarHeight / 2);

    const veil = new Graphics();
    veil.beginFill(COLORS.deep, 0.62);
    veil.drawRect(0, 0, viewportWidth, viewportHeight);
    veil.endFill();
    veil.lineStyle(1, COLORS.blood, 0.34);
    veil.moveTo(viewportWidth / 2 - 46, startY - 34);
    veil.lineTo(viewportWidth / 2, startY - 10);
    veil.lineTo(viewportWidth / 2 + 46, startY - 34);
    this.pillars.addChild(veil);

    const title = new Text("THE SIX OBSIDIAN PILLARS", new TextStyle({
      fill: "#d4af37",
      fontSize: mobile ? 12 : 18,
      fontFamily: "Cinzel Decorative, Georgia, serif",
      fontWeight: "700",
      align: "center"
    }));
    title.x = viewportWidth / 2 - title.width / 2;
    title.y = mobile ? 112 : startY - 52;
    this.pillars.addChild(title);

    pillars.forEach(([name, hotkey, content], index) => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const x = startX + column * (pillarWidth + gap);
      const y = startY + row * (pillarHeight + 22);
      const pillar = new Graphics();
      pillar.lineStyle(1, COLORS.gold, 1);
      pillar.beginFill(COLORS.obsidian, 0.94);
      pillar.drawPolygon([
        x + pillarWidth * 0.16, y + 22,
        x + pillarWidth * 0.5, y,
        x + pillarWidth * 0.84, y + 20,
        x + pillarWidth, y + pillarHeight - 24,
        x + pillarWidth * 0.78, y + pillarHeight,
        x + pillarWidth * 0.18, y + pillarHeight - 4,
        x, y + pillarHeight - 28
      ]);
      pillar.endFill();
      pillar.lineStyle(1, COLORS.blood, 0.86);
      pillar.moveTo(x + pillarWidth * 0.5, y + 18);
      pillar.lineTo(x + pillarWidth * 0.44, y + pillarHeight * 0.42);
      pillar.lineTo(x + pillarWidth * 0.58, y + pillarHeight * 0.68);
      pillar.lineTo(x + pillarWidth * 0.52, y + pillarHeight - 30);
      pillar.lineStyle(1, COLORS.ash, 0.32);
      pillar.moveTo(x + 14, y + pillarHeight - 28);
      pillar.lineTo(x + pillarWidth - 16, y + pillarHeight - 22);
      this.pillars.addChild(pillar);

      const rune = new Graphics();
      rune.lineStyle(1, COLORS.gold, 1);
      rune.beginFill(index === 0 ? COLORS.darkBlood : COLORS.deep, 0.86);
      this.drawJaggedDiamond(rune, x + pillarWidth / 2, y + 48, mobile ? 16 : 22);
      rune.endFill();
      this.pillars.addChild(rune);

      const nameText = new Text(name.toUpperCase(), new TextStyle({
        fill: "#d4af37",
        fontSize: mobile ? 8 : 11,
        fontFamily: "Cinzel Decorative, Georgia, serif",
        fontWeight: "700",
        align: "center",
        wordWrap: true,
        wordWrapWidth: pillarWidth - 20,
        lineHeight: mobile ? 10 : 13
      }));
      nameText.x = x + 10;
      nameText.y = y + (mobile ? 70 : 88);
      this.pillars.addChild(nameText);

      const contentText = new Text(content, new TextStyle({
        fill: "#ded0b8",
        fontSize: mobile ? 7 : 10,
        fontFamily: "Inter, sans-serif",
        wordWrap: true,
        wordWrapWidth: pillarWidth - 22,
        lineHeight: mobile ? 9 : 12
      }));
      contentText.x = x + 12;
      contentText.y = y + (mobile ? 96 : 132);
      this.pillars.addChild(contentText);

      const hotkeyText = new Text(hotkey, new TextStyle({
        fill: "#0a0a0f",
        fontSize: mobile ? 9 : 12,
        fontFamily: "Roboto Mono, monospace",
        fontWeight: "700",
        align: "center"
      }));
      const hotkeyPlate = new Graphics();
      hotkeyPlate.beginFill(COLORS.gold, 0.92);
      hotkeyPlate.drawPolygon([x + pillarWidth - 34, y + 14, x + pillarWidth - 12, y + 14, x + pillarWidth - 16, y + 34, x + pillarWidth - 38, y + 34]);
      hotkeyPlate.endFill();
      hotkeyText.x = x + pillarWidth - 30;
      hotkeyText.y = y + 17;
      this.pillars.addChild(hotkeyPlate, hotkeyText);
    });
  }

  private panel(x: number, y: number, width: number, height: number, alpha = PANEL_ALPHA, edge = COLORS.gold) {
    const g = new Graphics();
    g.lineStyle(1, edge, 0.72);
    g.beginFill(COLORS.obsidian, alpha);
    g.drawPolygon([x, y + 10, x + 10, y, x + width - 12, y, x + width, y + 16, x + width - 8, y + height, x + 8, y + height, x, y + height - 14]);
    g.endFill();
    g.lineStyle(1, COLORS.ash, 0.18);
    g.drawPolygon([x + 5, y + 14, x + 15, y + 6, x + width - 18, y + 6, x + width - 8, y + 18, x + width - 15, y + height - 7, x + 13, y + height - 7, x + 5, y + height - 18]);
    return g;
  }

  private bar(x: number, y: number, width: number, height: number, value: number, color: number) {
    const g = new Graphics();
    g.lineStyle(1, COLORS.ash, 0.9);
    g.beginFill(COLORS.deep, 0.86);
    g.drawPolygon([x, y, x + width, y, x + width - height, y + height, x + height, y + height]);
    g.endFill();
    const fill = Math.max(0, Math.min(1, value / 100)) * width;
    g.beginFill(color, 0.96);
    g.drawPolygon([x + 1, y + 1, x + fill, y + 1, Math.max(x + 1, x + fill - height), y + height - 1, x + height, y + height - 1]);
    g.endFill();
    this.shell.addChild(g);
  }

  private slot(x: number, y: number, size: number, color: number, label: string) {
    const g = new Graphics();
    g.lineStyle(1, COLORS.gold, 0.76);
    g.beginFill(COLORS.deep, 0.9);
    g.drawPolygon([x, y + 6, x + 6, y, x + size - 5, y, x + size, y + 7, x + size - 4, y + size, x + 5, y + size, x, y + size - 6]);
    g.endFill();
    g.beginFill(color, 0.7);
    g.drawPolygon([x + size * 0.5, y + 8, x + size - 9, y + size * 0.48, x + size * 0.5, y + size - 8, x + 9, y + size * 0.48]);
    g.endFill();
    this.shell.addChild(g);
    this.text(label, x + size / 2 - 8, y + size - 14, 10, COLORS.bone, 16, true, "center");
  }

  private objectiveRow(label: string, x: number, y: number, width: number, active: boolean, mobile: boolean) {
    const g = new Graphics();
    g.lineStyle(1, active ? COLORS.gold : COLORS.ash, 1);
    g.beginFill(active ? COLORS.darkBlood : COLORS.deep, active ? 0.65 : 0.38);
    this.drawJaggedDiamond(g, x + 8, y + 7, mobile ? 5 : 6);
    g.endFill();
    this.shell.addChild(g);
    this.text(label, x + 22, y, mobile ? 8 : 11, active ? COLORS.gold : COLORS.ash, width - 22, false);
  }

  private drawBuffStrip(x: number, y: number, count: number) {
    for (let index = 0; index < count; index += 1) {
      const g = new Graphics();
      const sx = x + index * 16;
      g.lineStyle(1, COLORS.ash, 0.7);
      g.beginFill(index % 2 === 0 ? COLORS.darkBlood : COLORS.deep, 0.8);
      this.drawJaggedDiamond(g, sx + 6, y + 6, 6);
      g.endFill();
      this.shell.addChild(g);
    }
  }

  private line(x: number, y: number, width: number, color: number, alpha: number) {
    const g = new Graphics();
    g.lineStyle(1, color, alpha);
    g.moveTo(x, y);
    g.lineTo(x + width / 2 - 8, y);
    g.lineTo(x + width / 2, y + 3);
    g.lineTo(x + width / 2 + 8, y);
    g.lineTo(x + width, y);
    this.shell.addChild(g);
  }

  private text(value: string, x: number, y: number, fontSize: number, color: number, width: number, title: boolean, align: "left" | "center" = "left") {
    const text = new Text(value, new TextStyle({
      fill: `#${color.toString(16).padStart(6, "0")}`,
      fontSize,
      fontFamily: title ? "Cinzel Decorative, Georgia, serif" : "Inter, sans-serif",
      fontWeight: title ? "700" : "400",
      wordWrap: true,
      wordWrapWidth: width,
      align,
      lineHeight: Math.ceil(fontSize * 1.25)
    }));
    text.x = x;
    text.y = y;
    this.shell.addChild(text);
    return text;
  }

  private drawJaggedDiamond(graphics: Graphics, cx: number, cy: number, radius: number) {
    graphics.drawPolygon([
      cx, cy - radius,
      cx + radius * 0.78, cy - radius * 0.2,
      cx + radius, cy,
      cx + radius * 0.58, cy + radius * 0.76,
      cx, cy + radius,
      cx - radius * 0.62, cy + radius * 0.72,
      cx - radius, cy,
      cx - radius * 0.72, cy - radius * 0.28
    ]);
  }

  private publishAudit() {
    (window as Window & { __soulsmithHudState?: HudAuditState }).__soulsmithHudState = this.auditState;
  }
}
